/**
 * User Mode Context
 * 
 * Manages the app's user mode for progressive disclosure of features.
 * - Personal Mode: Fitness/wellbeing focused - minimal medical features
 * - Caretaker Mode: Full medical profile access for caregivers monitoring dependents
 * - Clinical Mode: Additional clinical controls and immutable safety settings
 * 
 * All dependent health data is encrypted at rest via useEncryptedStorage
 * (AES-256-GCM, keyed to the user's auth session token).
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Preferences } from '@capacitor/preferences';
import { getEncrypted, setEncrypted } from '@/hooks/useEncryptedStorage';

// Storage key — mode is non-sensitive, profiles are sensitive
const USER_MODE_KEY = 'wellebit_user_mode';
const CARETAKER_PROFILES_KEY = 'wellebit_caretaker_profiles';

export type UserMode = 'personal' | 'caretaker' | 'clinical';

export interface DependentProfile {
  id: string;
  name: string;
  relationship: 'child' | 'parent' | 'relative' | 'patient';
  avatarEmoji?: string;
  // Profile data
  age?: number;
  weight?: number;
  height?: number;
  // Medical profile (paediatric/hospice)
  conditionIds: string[];
  medicationIds: string[];
  // Care settings
  fluidTargetMl?: number;
  isFluidRestricted?: boolean;
  fluidLimitMl?: number;
  // Clinical settings (can only be set/modified by clinical role)
  clinicalSettings?: {
    setBy: string;
    setAt: string;
    fluidLimitLocked?: boolean;
    alertThresholdsLocked?: boolean;
    notes?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface UserModeContextValue {
  // Current mode
  userMode: UserMode;
  setUserMode: (mode: UserMode) => Promise<void>;
  
  // Mode checks
  isCaretakerMode: boolean;
  isClinicalMode: boolean;
  showMedicalFeatures: boolean;
  showPaediatricFeatures: boolean;
  
  // Dependent profiles (for caretaker/clinical modes)
  dependents: DependentProfile[];
  activeDependentId: string | null;
  activeDependent: DependentProfile | null;
  setActiveDependentId: (id: string | null) => void;
  
  // CRUD for dependents
  addDependent: (profile: Omit<DependentProfile, 'id' | 'createdAt' | 'updatedAt'>) => Promise<DependentProfile>;
  updateDependent: (id: string, updates: Partial<DependentProfile>) => Promise<void>;
  removeDependent: (id: string) => Promise<void>;
  
  // Loading state
  isLoading: boolean;
}

const UserModeContext = createContext<UserModeContextValue | undefined>(undefined);

interface UserModeProviderProps {
  children: ReactNode;
}

export function UserModeProvider({ children }: UserModeProviderProps) {
  const [userMode, setUserModeState] = useState<UserMode>('personal');
  const [dependents, setDependents] = useState<DependentProfile[]>([]);
  const [activeDependentId, setActiveDependentId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load saved mode and dependents
  useEffect(() => {
    async function loadData() {
      try {
        // Mode is non-sensitive — plain Preferences
        let mode: UserMode | null = null;
        try {
          const modeRes = await Preferences.get({ key: USER_MODE_KEY });
          if (modeRes.value) mode = modeRes.value as UserMode;
        } catch {
          const m = localStorage.getItem(USER_MODE_KEY);
          if (m) mode = m as UserMode;
        }

        if (mode) setUserModeState(mode);

        // Dependent profiles are sensitive — encrypted storage
        const profiles = await getEncrypted<DependentProfile[]>(CARETAKER_PROFILES_KEY);
        if (profiles && profiles.length > 0) {
          setDependents(profiles);
          if (mode && mode !== 'personal') {
            setActiveDependentId(profiles[0].id);
          }
        }
      } catch (err) {
        console.error('[UserMode] Failed to load data:', err);
      } finally {
        setIsLoading(false);
      }
    }
    loadData();
  }, []);

  // Save helpers
  const saveMode = async (mode: UserMode) => {
    try {
      await Preferences.set({ key: USER_MODE_KEY, value: mode });
    } catch {
      localStorage.setItem(USER_MODE_KEY, mode);
    }
  };

  const saveDependents = async (profiles: DependentProfile[]) => {
    // Encrypted at rest via AES-256-GCM
    await setEncrypted(CARETAKER_PROFILES_KEY, profiles);
  };

  // Set user mode
  const setUserMode = useCallback(async (mode: UserMode) => {
    setUserModeState(mode);
    await saveMode(mode);
    
    // Clear active dependent if switching to personal
    if (mode === 'personal') {
      setActiveDependentId(null);
    } else if (dependents.length > 0 && !activeDependentId) {
      setActiveDependentId(dependents[0].id);
    }
  }, [dependents, activeDependentId]);

  // Add dependent
  const addDependent = useCallback(async (
    profile: Omit<DependentProfile, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<DependentProfile> => {
    const newProfile: DependentProfile = {
      ...profile,
      id: `dep_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    const updated = [...dependents, newProfile];
    setDependents(updated);
    await saveDependents(updated);

    // Auto-select if first dependent
    if (updated.length === 1) {
      setActiveDependentId(newProfile.id);
    }

    return newProfile;
  }, [dependents]);

  // Update dependent
  const updateDependent = useCallback(async (id: string, updates: Partial<DependentProfile>) => {
    const updated = dependents.map(dep => {
      if (dep.id === id) {
        // Don't allow modifying locked clinical settings unless in clinical mode
        if (dep.clinicalSettings?.fluidLimitLocked && updates.fluidLimitMl !== undefined && userMode !== 'clinical') {
          console.warn('[UserMode] Cannot modify locked fluid limit - clinical setting');
          delete updates.fluidLimitMl;
        }
        return {
          ...dep,
          ...updates,
          updatedAt: new Date().toISOString(),
        };
      }
      return dep;
    });

    setDependents(updated);
    await saveDependents(updated);
  }, [dependents, userMode]);

  // Remove dependent
  const removeDependent = useCallback(async (id: string) => {
    const updated = dependents.filter(dep => dep.id !== id);
    setDependents(updated);
    await saveDependents(updated);

    // Clear active if removed
    if (activeDependentId === id) {
      setActiveDependentId(updated.length > 0 ? updated[0].id : null);
    }
  }, [dependents, activeDependentId]);

  // Computed values
  const isCaretakerMode = userMode === 'caretaker' || userMode === 'clinical';
  const isClinicalMode = userMode === 'clinical';
  const showMedicalFeatures = userMode !== 'personal';
  const showPaediatricFeatures = isCaretakerMode && dependents.some(d => 
    d.conditionIds.length > 0 || d.medicationIds.length > 0
  );

  const activeDependent = activeDependentId 
    ? dependents.find(d => d.id === activeDependentId) || null 
    : null;

  const value: UserModeContextValue = {
    userMode,
    setUserMode,
    isCaretakerMode,
    isClinicalMode,
    showMedicalFeatures,
    showPaediatricFeatures,
    dependents,
    activeDependentId,
    activeDependent,
    setActiveDependentId,
    addDependent,
    updateDependent,
    removeDependent,
    isLoading,
  };

  return (
    <UserModeContext.Provider value={value}>
      {children}
    </UserModeContext.Provider>
  );
}

export function useUserMode(): UserModeContextValue {
  const context = useContext(UserModeContext);
  if (!context) {
    throw new Error('useUserMode must be used within a UserModeProvider');
  }
  return context;
}

export default UserModeContext;
